import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ModalShell } from '../../admin/modals/ModalShell'
import { useProcurementPartHistory } from './useDrilldown'

function formatDateShort(iso: string): string {
  const d = new Date(iso)
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}`
}

export function PartPriceHistoryModal({
  target,
  onClose,
}: {
  target: { partNumber: string; brand: string | null }
  onClose: () => void
}) {
  const { data, isLoading, isError } = useProcurementPartHistory(target)

  return (
    <ModalShell
      title={target.partNumber}
      subtitle={`${target.brand ?? '—'} · история цены по моим квотам`}
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Квот" value={data.stats.quotesCount} />
            <Stat
              label="Мин. цена"
              value={data.stats.minPrice === null ? '—' : data.stats.minPrice.toFixed(2)}
            />
            <Stat
              label="Ср. цена"
              value={data.stats.avgPrice === null ? '—' : data.stats.avgPrice.toFixed(2)}
            />
            <Stat
              label="Макс. цена"
              value={data.stats.maxPrice === null ? '—' : data.stats.maxPrice.toFixed(2)}
            />
          </div>

          {data.pricePoints.length > 0 && (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={120}>
                <LineChart
                  data={data.pricePoints.map((p) => ({
                    date: formatDateShort(p.date),
                    price: p.price,
                  }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e5e7eb',
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#3b82f6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Лучшие поставщики
            </div>
            <div className="space-y-1">
              {data.suppliers.slice(0, 10).map((s) => (
                <div key={s.name} className="flex items-center justify-between text-sm">
                  <span className="truncate text-gray-800">{s.name}</span>
                  <span className="text-xs text-gray-500 tabular-nums shrink-0 ml-2">
                    {s.count} · ср. {s.avgPrice.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
